'use client';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { useSidebar } from '@/contexts/SidebarContext';
import useBodyScrollLock from '@/hooks/useBodyScrollLock';

// Lazy-load the inquiry slide-over — it's heavy and only shown on user interaction
const InquirySubmitSlideOver = dynamic(
    () => import('@/components/layout/InquirySubmitSlideOver'),
    { ssr: false },
);

export default function OverlayRoot() {
    const { active, close, startClosing } = useSidebar();
    const [isClosing, setIsClosing] = useState(false);
    const [variantGroups, setVariantGroups] = useState(null);

    // Lazily load the variant lookup table only when the inquiry drawer is
    // opened, in parallel with the slide-over's own code chunk. This data
    // used to be threaded through the global ProductContext (and therefore
    // serialized into every route's RSC payload); loading it on-demand here
    // instead means every other page on the site no longer pays for it.
    useEffect(() => {
        if (active !== 'submit' || variantGroups) return;
        let cancelled = false;
        import('@/lib/productData.mjs').then((mod) => {
            if (!cancelled) setVariantGroups(mod.VARIANT_GROUPS ?? null);
        });
        return () => {
            cancelled = true;
        };
    }, [active, variantGroups]);

    // Define backdrop close callback before any early returns so hooks' order is stable
    const onBackdrop = useCallback(() => {
        // close whichever is active
        startClosing();
        setIsClosing(true);
        setTimeout(() => {
            close();
            setIsClosing(false);
        }, 300);
    }, [close, startClosing]);

    // Body lock when any overlay is active
    useBodyScrollLock('overlay-root', Boolean(active));

    useEffect(() => {
        // reset closing when re-opened
        if (active) setIsClosing(false);
    }, [active]);

    // Close on Escape
    useEffect(() => {
        if (!active) return;
        const onKey = (e) => {
            if (e.key === 'Escape') onBackdrop();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [active, onBackdrop]);

    if (!active && !isClosing) return null;

    return (
        <div className="fixed inset-0 z-70">
            {/* Backdrop shared for all overlays */}
            <button
                type="button"
                aria-label="Close overlay"
                className={`absolute inset-0 bg-black/45 backdrop-blur-sm z-40 border-none cursor-default ${
                    isClosing || !active
                        ? 'opacity-0 transition-opacity duration-300 ease-out'
                        : 'opacity-50 animate-fadeInBackdrop'
                }`}
                onClick={onBackdrop}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') onBackdrop();
                }}
            />

            {/* Right slide panel area */}
            <div className="absolute right-0 top-0 h-full z-50">
                {active === 'submit' && (
                    <div
                        className={`${
                            isClosing
                                ? 'transform translate-x-full transition-transform duration-300 ease-out'
                                : 'transform translate-x-0 animate-slideInFromRight'
                        }`}
                    >
                        <InquirySubmitSlideOver
                            onRequestClose={onBackdrop}
                            variantGroups={variantGroups}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
